<template>
  <div>
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      location="bottom"
      :offset="[0, 4]"
      :disabled="disabled"
      min-width="auto"
    >
      <template #activator="{ props: menuProps }">
        <InputText
          :model-value="displayValue"
          v-bind="{ ...$attrs, ...menuProps }"
          :disabled="disabled"
          append-icon="mdi-calendar"
          placeholder="dd.mm.yy"
          maxlength="20"
          @keydown="onKeydown"
          @update:modelValue="onInput"
        />
      </template>
      <v-date-picker
        :model-value="dateIso"
        :year="displayYear"
        :month="displayMonth"
        :view-mode="viewMode"
        :max="maxDate"
        :disabled="disabled"
        color="primary"
        min="1890-01-01"
        @update:year="onYearUpdate"
        @update:month="onMonthUpdate"
        @update:model-value="onDateSelectedIso"
        @click.capture="onPickerCapture"
      />
    </v-menu>
  </div>
</template>

<script setup lang="ts">
import {computed, onBeforeUnmount, ref, watch} from "vue";
import InputText from "@/components/input-components/InputText.vue";
import {dateToIso, formatDisplayDate, parseDateString} from "@/composables/useDateHelper";
import {useVuetifyDatePickerFix} from "@/composables/useControlRenderer";
import {PickerView} from '@/composables/useDateHelper';

const props = defineProps({
  modelValue: { type: String, default: null },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue"]);

const viewMode = ref<PickerView.Year | PickerView.Months | PickerView.Date>(PickerView.Year)
const displayYear = ref(new Date().getFullYear())

const {
  onPickerCapture,
} = useVuetifyDatePickerFix(
  viewMode,
  displayYear
)

const dateIso = ref<string | null>(null)
const displayMonth = ref(new Date().getMonth())
const menu = ref(false)
const displayValue = ref('')
const expandTimer = ref<number>(0)
const modelValue = ref(null)

const maxDate = computed(() => {
  const now = new Date();
  return `${now.getFullYear() + 1}-${String(now.getMonth() + 1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
});

watch(() => props.modelValue, (val) => {
  if (!val) {
    displayValue.value = ""
    dateIso.value = null
    return
  }

  if (dateIso.value === val) return
  displayValue.value = formatDisplayDate(val) ?? ""
  dateIso.value = val

  const parts = val.split('-')
  if (parts.length === 3) {
    displayYear.value = parseInt(parts[0])
    displayMonth.value = parseInt(parts[1]) - 1
  }
}, { immediate: true })

function onDateSelectedIso(val: string | Date | null) {
  if (!val) {
    displayValue.value = ""
    dateIso.value = null
    emit("update:modelValue", null)
    return
  }

  let iso: string

  if (typeof val === "string") {
    iso = val
  } else {
    iso = dateToIso(val)
  }

  dateIso.value = iso
  displayValue.value = formatDisplayDate(iso) ?? ""
  emit("update:modelValue", iso)

  menu.value = false
}

function onInput(val: string) {
  if (expandTimer.value) {
    clearTimeout(expandTimer.value)
    expandTimer.value = 0
  }

  displayValue.value = val

  // Text date:
  // 1 apr 20
  // 01 apr 2020
  // 1 april 1980
  if (/[a-zA-ZäöüÄÖÜß]/.test(val)) {
    // wait on input
    expandTimer.value = window.setTimeout(() => {
      if (displayValue.value !== val) return
      commitInputDate()
    }, 1500)
    return
  }

  const digits = val.replace(/\D/g, '').slice(0, 8)

  // Numeric date without separators.
  if (digits.length > 4) {
    displayValue.value = `${digits.slice(0,2)}.${digits.slice(2,4)}.${digits.slice(4)}`
  } else if (digits.length > 2) {
    displayValue.value = `${digits.slice(0,2)}.${digits.slice(2)}`
  } else {
    displayValue.value = digits
  }

  // DDMMYYYY
  if (digits.length === 8) {
    commitInputDate()
    return
  }

  // DDMMYY
  if (digits.length === 6) {
    expandTimer.value = window.setTimeout(() => {
      const currentDigits = displayValue.value.replace(/\D/g, '')
      if (currentDigits.length !== 6) return
      commitInputDate()
    }, 700)
  }
}

function onKeydown(event: KeyboardEvent) {
  if ((event.ctrlKey && event.key === "Enter") || event.key === "Tab") {
    const committed = commitInputDate()

    if (!committed) {
      return
    }

    menu.value = false

    const inputElement = event.currentTarget as HTMLInputElement
    const form = inputElement.form

    if (!form) {
      return
    }

    const elements = Array.from(form.elements) as HTMLElement[]
    const index = elements.indexOf(inputElement)

    if (index === -1) {
      return
    }

    const next = elements[index + 1]

    if (next) {
      next.focus()
      event.preventDefault()
    }
  }
}

function onYearUpdate(year: number) {
  displayYear.value = year
  viewMode.value = PickerView.Months
}

function onMonthUpdate(month: number) {
  displayMonth.value = month
  viewMode.value = PickerView.Date
}

function commitInputDate(): boolean {
  const parsed = parseDateString(displayValue.value)

  if (
    parsed?.year === undefined ||
    parsed.month === undefined ||
    parsed.day === undefined
  ) {
    return false
  }

  const iso =
    `${String(parsed.year).padStart(4, "0")}-` +
    `${String(parsed.month).padStart(2, "0")}-` +
    `${String(parsed.day).padStart(2, "0")}`

  dateIso.value = iso
  displayYear.value = parsed.year
  displayMonth.value = parsed.month - 1
  displayValue.value = formatDisplayDate(iso) ?? displayValue.value

  emit("update:modelValue", iso)

  return true
}

watch(menu, (val) => {
  if (val) {
    viewMode.value = PickerView.Year
  }
})

onBeforeUnmount(() => {
  if (expandTimer.value) {
    clearTimeout(expandTimer.value)
    expandTimer.value = 0
  }
  viewMode.value = PickerView.Year
});
</script>

<style lang="scss" scoped>
:deep(.v-date-picker-controls) {
  display: none !important;
}
:deep(.v-date-picker-controls__month-btn),
:deep(.v-date-picker-controls__mode-btn) {
  display: none !important;
}

.v-date-picker-month__day-btn {
  .v-btn__overlay {
    opacity: 0 !important;
    transition: none !important;
    background-color: currentColor !important;
    min-width: initial !important;
    --v-btn-height: 1.5rem !important;
  }

  &:hover .v-btn__overlay {
    opacity: var(--v-hover-opacity, 0.3) !important;
  }

  &--selected .v-btn__overlay {
    opacity: var(--v-hover-opacity, 0.3) !important;
    color: rgb(var(--v-theme-white));
    background-color: currentColor !important;
    border: initial;
  }
}

.v-date-picker-years .v-btn:hover .v-btn__overlay,
.v-date-picker-months .v-btn:hover .v-btn__overlay {
  opacity: var(--v-hover-opacity, 0.3) !important;
}

</style>
